import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import { productionKeys, workCenterDetailQuery, workCenterKinds, workCenterListQuery, type WorkCenterRow } from "@/features/production/api";
import { WorkCenterPanel } from "@/features/production/WorkCenterPanel";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";
import { workCenterListColumns } from "@patronix/domain/list";
import { filteredQuery, type ClientColumn } from "@/lib/clientList";
import { listView, viewSearch, type ViewSearch } from "@/lib/listView";

// Work centers of the active company, one page (a plant has dozens at most):
// one request for the list (embeds supply warehouse / location codes), one per
// detail (prefetched on hover). Writes are table writes under RLS; bulk
// changes by import (ADR-034).

interface Search extends ViewSearch {
  inactive: boolean;
  wc?: string | undefined;
  /** The import panel is open (replaces the work center detail). */
  import?: "work_centers" | undefined;
}

const invalidateAfterImport = [productionKeys.all] as const;

const clientColumns: ClientColumn<WorkCenterRow>[] = [
  { key: "code", type: "text", value: (r) => r.code },
  { key: "name", type: "text", value: (r) => r.name },
  { key: "kind", type: "enum", value: (r) => r.kind },
  { key: "supply", type: "text", value: (r) => (r.supply_warehouse ? `${r.supply_warehouse.code} ${r.supply_location?.code ?? ""}` : null) },
  { key: "hours", type: "number", value: (r) => Number(r.hours_per_day) },
];

export const Route = createFileRoute("/_app/manufacturing/work-centers")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = { inactive: raw.inactive === true || raw.inactive === "true" };
    if (typeof raw.wc === "string" && raw.wc) s.wc = raw.wc;
    if (raw.import === "work_centers") s.import = raw.import;
    return { ...s, ...viewSearch(workCenterListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ inactive: search.inactive }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(workCenterListQuery(companyId, deps.inactive));
  },
  component: WorkCentersPage,
});

function WorkCentersPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("production.master.manage");
  const canImport = useCan("core.data.import") && canManage;
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  // All work centres of the company are loaded: column filters and sort run in the browser.
  const view = listView(workCenterListColumns, search, setSearch);
  const query = filteredQuery(useInfiniteQuery(workCenterListQuery(company?.id ?? "", search.inactive)), clientColumns, view.query.filters, view.query.sort);

  const columns: Column<WorkCenterRow>[] = [
    {
      key: "code",
      header: t("wc.code"),
      width: "minmax(100px,0.7fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.code}</span>,
    },
    {
      key: "name",
      header: t("wc.name"),
      width: "minmax(180px,1.6fr)",
      cell: (r) => <span className={cn(!r.is_active && "text-ink-muted line-through decoration-ink-faint")}>{r.name}</span>,
    },
    {
      key: "kind",
      header: t("wc.kind"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => <Badge tone="neutral">{t(`wc.kinds.${r.kind}`)}</Badge>,
      options: workCenterKinds.map((v) => ({ value: v, label: t(`wc.kinds.${v}`) })),
    },
    {
      key: "supply",
      header: t("wc.supply"),
      width: "minmax(130px,1fr)",
      className: "text-ink-soft",
      cell: (r) => (r.supply_warehouse ? `${r.supply_warehouse.code}${r.supply_location ? ` · ${r.supply_location.code}` : ""}` : "—"),
    },
    {
      key: "hours",
      header: t("wc.hoursPerDay"),
      width: "minmax(90px,0.6fr)",
      align: "end",
      cell: (r) => <span className="tabular">{fmt.qty(String(r.hours_per_day))} h</span>,
    },
  ];

  const listTools: ListTools<WorkCenterRow> = {
    id: "work-centers",
    title: t("wc.title"),
    onNew: canManage ? () => setSearch({ wc: "new", import: undefined }) : undefined,
    onImport: canImport ? () => setSearch({ import: "work_centers", wc: undefined }) : undefined,
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.manufacturing.title")} <span aria-hidden="true">/</span> {t("nav.manufacturing.workCenters")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("wc.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ wc: "new", import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("wc.new")}
          </button>
        )}
      </header>
      <label className="flex items-center gap-2 text-[13px] text-ink-soft">
        <input
          type="checkbox"
          checked={search.inactive}
          onChange={(e) => setSearch({ inactive: e.target.checked })}
          className="size-4 accent-(--accent)"
        />
        {t("wc.showInactive")}
      </label>
      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("wc.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.wc}
          onSelect={(r) => setSearch({ wc: r.id, import: undefined })}
          onClear={() => setSearch({ wc: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(workCenterDetailQuery(r.id))}
          emptyTitle={t("wc.emptyTitle")}
          emptyBody={t("wc.emptyBody")}
          emptyAction={
            canManage ? (
              <button
                type="button"
                onClick={() => setSearch({ wc: "new" })}
                className="h-9 rounded-xl bg-accent px-3.5 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
              >
                {t("wc.new")}
              </button>
            ) : undefined
          }
          minWidth={620}
          tools={listTools}
          view={view}
        />
        <DetailPanel
          label={search.import ? t("import.title") : t("wc.detailLabel")}
          open={!!search.wc || !!search.import}
          placeholder={<DetailPlaceholder title={t("wc.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={["work_centers"]}
              initial="work_centers"
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={invalidateAfterImport}
            />
          ) : (
            search.wc && (
              <WorkCenterPanel
                key={search.wc}
                id={search.wc}
                onClose={() => setSearch({ wc: undefined })}
                onCreated={(id) => setSearch({ wc: id })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
